import { ClaimWorkflowConfigurationOption, ClaimWorkflowConfigurationOptions, ProviderCreateDocumentType, SupportDocumentType } from '../../api';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { sanitizeFilename } from '../../services/primitives/filename-utils';
import { CoreDocumentsStore } from '../documents.store';
import {
    createEmptyAutomationWorkers,
    isAutomationRulesOptionKey,
    isAutomationWorkerWorkflowStatus,
    mergeWorkflowConfiguration,
    readAutomationWorkerEnabled,
    splitWorkflowConfiguration,
} from '../../services/worker-configuration/automation/worker-automation.parser';
import {
    AutomationRule,
    AutomationWorker,
    AutomationWorkerId,
    automationWorkersEqual,
    CatalogDocument,
    CatalogFacility,
    CatalogPayer,
    CatalogProvider,
    CatalogUsage,
    cloneAutomationWorkers,
    CoderRuleSummary,
    ConditionKind,
    DateOfServiceMatchValue,
    EnterCatalog,
    ReconsiderationDocument,
    RuleActionType,
    RuleMatch,
    RuleMatchScope,
} from '../../services/worker-configuration/automation/worker-automation.types';
import { compareWorkflowStatusExecutionOrder } from '../../ui/worker-configuration/configuration-worker-execution-order';
import {
    addConditionValue,
    canExcludeScope,
    createEmptyMatch,
    getAvailableMatchScopes,
    getConditionValues,
    removeConditionValue,
    replaceConditionValues,
    setDateOfServiceMatchValue,
} from '../../services/worker-configuration/automation/worker-automation-scope.registry';
import {
    clampRuleName,
    createEmptyRule,
    createUniqueDefaultRuleName,
    formatRuleMatchSummary,
    getAutomationActionValidationError,
    getAutomationMatchValidationError,
    getCatalogValueUsage,
    getDefaultActionValue,
    getMatchBlockValidationError,
    getRuleActionValidationError,
    getRuleNameValidationError,
    normalizeDisabledCodingRules,
    resolveRuleActionType,
} from '../../services/worker-configuration/automation/worker-automation.utils';
import {
    buildWorkerFunctionContextJson,
    buildWorkerFunctionUtility,
    checkWorkerFunctionSyntax,
    extractGeneratedFunction,
} from '../../services/worker-configuration/automation/worker-function-prompt';
import { aiBridge } from '../../services/ai-adapter/bridge';
import { buildAiConversationId } from '../../services/ai-adapter/protocol';
import { getCarcPairRequirements } from '../../services/worker-configuration/automation/worker-automation-carc-pair';
import {
    AssignableUser,
    CatalogProviderRole,
    canEditWorkflowOption,
    isRetiredWorkflowOption,
    fetchCatalogFacilitiesByIds,
    fetchCatalogPayersByIds,
    fetchCatalogProvidersByIds,
    fetchClaimWorkflowConfiguration,
    fetchClaimWorkflowConfigurationOptions,
    fetchAssignableUsers,
    fetchCoderRules,
    fetchEnterCatalog,
    formatCatalogCountLabel,
    formatCatalogSyncLabel,
    hasPersistedConfiguration,
    resolveAssignableUserInput,
    saveClaimWorkflowConfiguration,
    searchCatalogDocuments,
    searchCatalogFacilities,
    searchCatalogPayers,
    searchCatalogProviders,
    WorkerConfigurationApiMode,
} from '../../services/worker-configuration';
import {
    cloneWorkflowConfigMap,
    applyWorkflowOptionDefaults,
    coerceNumericWorkflowConfigValues,
    isUnsetConfigValue,
    parseWorkerWorkflowConfiguration,
    setWorkflowConfigValue,
    statusHasConfiguredOptions,
    WorkerWorkflowConfiguration,
    WorkflowConfigMap,
    WorkflowConfigValue,
    workflowConfigsEqual,
} from '../../services/worker-configuration/worker-configuration.types';
export type WorkerSelectionType = 'status' | 'automation';

export interface WorkerStoreOptions {
    apiMode?: WorkerConfigurationApiMode;
    readOnly?: boolean;
}

/** One rule's AI "Build the function" state — see `WorkerStore.functionBuilders`. */
export interface FunctionBuilderState {
    building: boolean;
    error: string | null;
    /** 'prompt': composing/editing a description. 'code': a build succeeded; code is in the editor. */
    mode: 'prompt' | 'code';
    prompt: string;
}

const DEFAULT_FUNCTION_BUILDER_STATE: Omit<FunctionBuilderState, 'mode'> = {
    building: false,
    error: null,
    prompt: '',
};

export class WorkerStore {
    public readonly apiMode: WorkerConfigurationApiMode;
    public readonly readOnly: boolean;

    @observable public loading: boolean = false;
    @observable public saving: boolean = false;
    @observable public configuration: WorkerWorkflowConfiguration | null = null;
    @observable public configurationOptions: ClaimWorkflowConfigurationOptions[] = [];
    @observable public error: string | null = null;
    @observable public saveError: string | null = null;
    @observable public selectedStatus: string | null = null;
    @observable public selectedWorkerType: WorkerSelectionType = 'status';
    @observable public selectedAutomationWorkerId: AutomationWorkerId | null = null;
    @observable public draftConfig: WorkflowConfigMap = {};
    @observable public draftAutomationWorkers: Record<AutomationWorkerId, AutomationWorker> =
        createEmptyAutomationWorkers();
    @observable public catalog: EnterCatalog | null = null;
    @observable public catalogImporting: boolean = false;
    @observable public importedAtLabel: string = 'Not synced';
    @observable public expandedRuleId: string | null = null;
    @observable public openPickerKey: string | null = null;
    @observable public pickerQuery: string = '';
    @observable public pickerSearchLoading: boolean = false;
    @observable public pickerPayerOptions: CatalogPayer[] = [];
    @observable public pickerProviderOptions: CatalogProvider[] = [];
    @observable public pickerFacilityOptions: CatalogFacility[] = [];
    @observable public pickerDocumentOptions: CatalogDocument[] = [];
    @observable public documentUploading: boolean = false;
    @observable public payerById: Record<string, CatalogPayer> = {};
    @observable public providerById: Record<string, CatalogProvider> = {};
    @observable public facilityById: Record<string, CatalogFacility> = {};
    @observable public draggingRuleId: string | null = null;
    @observable public dragOverRuleId: string | null = null;
    @observable public dirty: boolean = false;
    @observable public assignableUsers: AssignableUser[] = [];
    @observable public assignableUsersLoading: boolean = false;
    @observable public coderRules: CoderRuleSummary[] = [];
    @observable public coderRulesLoading: boolean = false;
    /**
     * Separate from `coderRules.length > 0` on purpose (PR review, 2026-09-28): an account with
     * zero coder rules is a legitimately loaded, empty result — using array length as the "have we
     * fetched yet" signal re-issued the request every time a function rule was expanded.
     */
    @observable public coderRulesLoaded: boolean = false;
    /**
     * A failed fetch also leaves `coderRules` at `[]` (see `loadCoderRules`'s catch branch) —
     * without this, that is indistinguishable from an account that genuinely has zero coder rules,
     * and the picker would say "No coder rules on this account" when the truth is "couldn't load
     * them" (PR review, 2026-09-28). Cleared at the start of every load attempt.
     */
    @observable public coderRulesError: string | null = null;
    /**
     * The AI "Build the function" flow, keyed by rule id — deliberately NOT saved with the rule
     * (product decision, 2026-09-24): the prompt and build state are working-session scratch, lost
     * on reload same as an unsaved draft. `function` itself lands in the rule via the normal
     * `setRuleActionValue` path once a build succeeds, so it IS saved.
     */
    @observable public functionBuilders: Record<string, FunctionBuilderState> = {};

    private savedConfig: WorkflowConfigMap = {};
    private savedAutomationWorkers: Record<AutomationWorkerId, AutomationWorker> = createEmptyAutomationWorkers();
    private pickerSearchRequestId = 0;
    /** Per-rule build counter — a stale response (rule deleted / another build started) is dropped. */
    private functionBuildRequestId: Record<string, number> = {};
    private readonly documentsStore: CoreDocumentsStore;

    constructor(options: WorkerStoreOptions = {}) {
        this.apiMode = options.apiMode ?? 'support';
        this.readOnly = WorkerStore.resolveReadOnly(options.readOnly);
        this.documentsStore = new CoreDocumentsStore({ supportGrant: this.apiMode === 'support' });
        makeObservable(this);
    }

    /** Support local preview: add ?workerConfigReadOnly=1 to the URL (hard refresh after toggling). */
    private static resolveReadOnly(explicit?: boolean): boolean {
        if (explicit === true) {
            return true;
        }

        if (explicit === false) {
            if (typeof window !== 'undefined') {
                const params = new URLSearchParams(window.location.search);
                if (params.get('workerConfigReadOnly') === '1') {
                    return true;
                }
            }
            return false;
        }

        return false;
    }

    private canMutate(): boolean {
        return !this.readOnly;
    }

    @computed
    public get isInteractionDisabled(): boolean {
        return this.readOnly || this.saving;
    }

    /**
     * True when the only pending diff is option defaults from the options endpoint
     * (new empty configs seeded for create, or existing configs with unset keys filled).
     */
    @computed
    public get hasRecommendedDefaultUpdatesOnly(): boolean {
        if (!this.dirty) {
            return false;
        }

        if (!automationWorkersEqual(this.draftAutomationWorkers, this.savedAutomationWorkers)) {
            return false;
        }

        const withDefaults = applyWorkflowOptionDefaults(this.savedConfig, this.configurationOptions, this.apiMode);
        return (
            workflowConfigsEqual(this.draftConfig, withDefaults) &&
            !workflowConfigsEqual(this.draftConfig, this.savedConfig)
        );
    }

    /**
     * The rules as SAVED — what the worker actually runs — never the draft.
     *
     * For surfaces outside the Workers page that report coverage (the denials page, FE-116): a rule
     * someone is half-way through editing there must not read as covering anything. Read-only and
     * additive; the Workers page keeps working on the draft.
     */
    public getSavedAutomationRules = (workerId: AutomationWorkerId): AutomationRule[] => {
        // `savedAutomationWorkers` is not observable; `configuration` is, and every fetch and save
        // that replaces the saved workers also sets it. Reading it here is what lets an observer that
        // calls this re-render after a load, without changing how the existing field is tracked.
        void this.configuration;
        return this.savedAutomationWorkers[workerId]?.rules || [];
    };

    @computed
    public get selectedAutomationWorker(): AutomationWorker | undefined {
        if (!this.selectedAutomationWorkerId) {
            return undefined;
        }

        return this.draftAutomationWorkers[this.selectedAutomationWorkerId];
    }

    @computed
    public get isAutomationWorkerSelected(): boolean {
        return this.selectedWorkerType === 'automation' && this.selectedAutomationWorkerId !== null;
    }

    @computed
    public get automationWorkers() {
        return (Object.values(this.draftAutomationWorkers) as AutomationWorker[]).map(worker => ({
            active: readAutomationWorkerEnabled(this.draftConfig, worker),
            desc: worker.desc,
            icon: worker.icon,
            id: worker.id,
            key: worker.key,
            name: worker.name,
            ruleCount: worker.rules.length,
            scopeLabel: worker.scopeLabel,
        }));
    }

    public getSelectedAutomationOptionDefinitions = (): Array<
        ClaimWorkflowConfigurationOption & { key: string }
    > => {
        const worker = this.selectedAutomationWorker;
        if (!worker) {
            return [];
        }

        const statusOptions = this.configurationOptions.find(
            option => option.workflowStatus === worker.key,
        );

        return (statusOptions?.options ?? [])
            .filter(
                (option): option is ClaimWorkflowConfigurationOption & { key: string } =>
                    Boolean(option.key?.trim()) &&
                    !isAutomationRulesOptionKey(option.key) &&
                    !isRetiredWorkflowOption(worker.key, option.key!.trim()),
            );
    };

    @computed
    public get catalogCountLabel(): string {
        return this.catalog ? formatCatalogCountLabel(this.catalog) : '';
    }

    @computed
    public get sortedStatuses(): ClaimWorkflowConfigurationOptions[] {
        return [...this.configurationOptions]
            .filter(
                option =>
                    Boolean(option.workflowStatus?.trim()) &&
                    !isAutomationWorkerWorkflowStatus(option.workflowStatus),
            )
            .sort((left, right) =>
                compareWorkflowStatusExecutionOrder(left.workflowStatus!, right.workflowStatus!),
            );
    }

    @computed
    public get selectedStatusOptions(): ClaimWorkflowConfigurationOptions | undefined {
        if (!this.selectedStatus) {
            return undefined;
        }
        return this.configurationOptions.find(option => option.workflowStatus === this.selectedStatus);
    }

    @computed
    public get selectedStatusOptionCounts(): { configured: number; total: number } {
        const status = this.selectedStatus;
        const options = this.selectedStatusOptions?.options ?? [];
        const validOptions = options.filter(option => Boolean(option.key?.trim()));
        const total = validOptions.length;

        if (!status || total === 0) {
            return { configured: 0, total };
        }

        const configured = validOptions.filter(
            option => !isUnsetConfigValue(this.getConfigValue(status, option.key!)),
        ).length;

        return { configured, total };
    }

    public hasConfig(status?: string): boolean {
        if (!status) {
            return false;
        }

        return statusHasConfiguredOptions(this.draftConfig[status]);
    }

    public getConfigValue(status: string, key: string): WorkflowConfigValue | undefined {
        return this.draftConfig[status]?.[key];
    }

    @action
    public setSelectedStatus = (status: string | null): void => {
        this.selectedWorkerType = 'status';
        this.selectedAutomationWorkerId = null;
        this.selectedStatus = status;
        this.expandedRuleId = null;
        this.openPickerKey = null;
    };

    @action
    public setSelectedAutomationWorker = (workerId: AutomationWorkerId): void => {
        this.selectedWorkerType = 'automation';
        this.selectedAutomationWorkerId = workerId;
        this.selectedStatus = null;
        this.expandedRuleId = null;
        this.openPickerKey = null;
    };

    @action
    public setOptionValue = (status: string, key: string, value: WorkflowConfigValue | null): void => {
        if (!this.canMutate() || !canEditWorkflowOption(this.apiMode, status, key)) {
            return;
        }

        this.draftConfig = setWorkflowConfigValue(this.draftConfig, status, key, value);
        this.markDirty();
        this.saveError = null;
    };

    public canEditOption = (status: string, key: string): boolean => {
        return this.canMutate() && canEditWorkflowOption(this.apiMode, status, key);
    };

    @action
    public clearOptionValue = (status: string, key: string): void => {
        this.setOptionValue(status, key, null);
    };

    @action
    public toggleRuleExpanded = (ruleId: string): void => {
        const expanding = this.expandedRuleId !== ruleId;
        this.expandedRuleId = expanding ? ruleId : null;
        if (expanding) {
            void this.loadAssignableUsers();
            if (resolveRuleActionType(this.findRule(ruleId)?.action.type) === 'EXECUTE_ENTER_FUNCTION') {
                void this.loadCoderRules();
            }
        }
    };

    @action
    public setPickerState = (pickerKey: string | null, query: string = ''): void => {
        if (!this.canMutate() && pickerKey !== null) {
            return;
        }

        const opening = pickerKey !== null && pickerKey !== this.openPickerKey;
        this.openPickerKey = pickerKey;
        this.pickerQuery = query;
        if (!pickerKey) {
            this.pickerPayerOptions = [];
            this.pickerProviderOptions = [];
            this.pickerFacilityOptions = [];
            this.pickerDocumentOptions = [];
            this.pickerSearchLoading = false;
            return;
        }

        if (opening) {
            this.pickerPayerOptions = [];
            this.pickerProviderOptions = [];
            this.pickerFacilityOptions = [];
            this.pickerDocumentOptions = [];
            this.pickerSearchLoading =
                pickerKey.includes('|payer') ||
                pickerKey.includes('|provider') ||
                pickerKey.includes('|facility') ||
                pickerKey.includes('|documents');
        }
    };

    @action
    private cachePayers = (payers: CatalogPayer[]): void => {
        for (const payer of payers) {
            this.payerById[payer.payerId] = payer;
        }
    };

    @action
    private cacheProviders = (providers: CatalogProvider[]): void => {
        for (const provider of providers) {
            this.providerById[provider.providerId] = provider;
        }
    };

    @action
    private cacheFacilities = (facilities: CatalogFacility[]): void => {
        for (const facility of facilities) {
            this.facilityById[facility.facilityId] = facility;
        }
    };

    public getPayerLabel = (payerId: string): string => {
        return this.payerById[payerId]?.name ?? payerId;
    };

    public getProviderLabel = (providerId: string): string => {
        return this.providerById[providerId]?.name ?? providerId;
    };

    public getFacilityLabel = (facilityId: string): string => {
        return this.facilityById[facilityId]?.name ?? facilityId;
    };

    public addPayer = (ruleId: string, matchIndex: number, payer: CatalogPayer): void => {
        this.cachePayers([payer]);
        this.addConditionChip(ruleId, matchIndex, 'payerId', payer.payerId);
    };

    public removePayer = (ruleId: string, matchIndex: number, payerId: string): void => {
        const worker = this.selectedAutomationWorker;
        const rule = worker?.rules.find(entry => entry.id === ruleId);
        const match = rule?.match[matchIndex];
        if (!match) {
            return;
        }

        const index = getConditionValues(match, 'payerId').indexOf(payerId);
        if (index >= 0) {
            this.removeConditionChip(ruleId, matchIndex, 'payerId', index);
        }
    };

    public addProvider = (
        ruleId: string,
        matchIndex: number,
        kind: 'billingProviderId' | 'renderingProviderId',
        provider: CatalogProvider,
    ): void => {
        this.cacheProviders([provider]);
        this.addConditionChip(ruleId, matchIndex, kind, provider.providerId);
    };

    public removeProvider = (
        ruleId: string,
        matchIndex: number,
        kind: 'billingProviderId' | 'renderingProviderId',
        providerId: string,
    ): void => {
        const worker = this.selectedAutomationWorker;
        const rule = worker?.rules.find(entry => entry.id === ruleId);
        const match = rule?.match[matchIndex];
        if (!match) {
            return;
        }

        const index = getConditionValues(match, kind).indexOf(providerId);
        if (index >= 0) {
            this.removeConditionChip(ruleId, matchIndex, kind, index);
        }
    };

    public addFacility = (ruleId: string, matchIndex: number, facility: CatalogFacility): void => {
        this.cacheFacilities([facility]);
        this.addConditionChip(ruleId, matchIndex, 'facilityId', facility.facilityId);
    };

    public removeFacility = (ruleId: string, matchIndex: number, facilityId: string): void => {
        const worker = this.selectedAutomationWorker;
        const rule = worker?.rules.find(entry => entry.id === ruleId);
        const match = rule?.match[matchIndex];
        if (!match) {
            return;
        }

        const index = getConditionValues(match, 'facilityId').indexOf(facilityId);
        if (index >= 0) {
            this.removeConditionChip(ruleId, matchIndex, 'facilityId', index);
        }
    };

    public setDateOfServiceRange = (
        ruleId: string,
        matchIndex: number,
        patch: Partial<DateOfServiceMatchValue>,
    ): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            match: rule.match.map((match, index) =>
                index === matchIndex ? setDateOfServiceMatchValue(match, patch) : match,
            ),
        }));
    };

    public rememberProviders = (providers: CatalogProvider[]): void => {
        this.cacheProviders(providers);
    };

    public rememberPayers = (payers: CatalogPayer[]): void => {
        this.cachePayers(payers);
    };

    public rememberFacilities = (facilities: CatalogFacility[]): void => {
        this.cacheFacilities(facilities);
    };

    public searchPickerPayers = async (query: string): Promise<void> => {
        const requestId = ++this.pickerSearchRequestId;

        runInAction(() => {
            this.pickerSearchLoading = true;
        });

        try {
            const payers = await searchCatalogPayers(query, this.apiMode);
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerPayerOptions = payers;
                this.cachePayers(payers);
                this.pickerSearchLoading = false;
            });
        } catch {
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerPayerOptions = [];
                this.pickerSearchLoading = false;
            });
        }
    };

    public searchPickerProviders = async (query: string, role: CatalogProviderRole): Promise<void> => {
        const requestId = ++this.pickerSearchRequestId;

        runInAction(() => {
            this.pickerSearchLoading = true;
        });

        try {
            const providers = await searchCatalogProviders(query, role, this.apiMode);
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerProviderOptions = providers;
                this.cacheProviders(providers);
                this.pickerSearchLoading = false;
            });
        } catch {
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerProviderOptions = [];
                this.pickerSearchLoading = false;
            });
        }
    };

    public searchPickerFacilities = async (query: string): Promise<void> => {
        const requestId = ++this.pickerSearchRequestId;

        runInAction(() => {
            this.pickerSearchLoading = true;
        });

        try {
            const facilities = await searchCatalogFacilities(query, this.apiMode);
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerFacilityOptions = facilities;
                this.cacheFacilities(facilities);
                this.pickerSearchLoading = false;
            });
        } catch {
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerFacilityOptions = [];
                this.pickerSearchLoading = false;
            });
        }
    };

    public searchPickerDocuments = async (query: string): Promise<void> => {
        const requestId = ++this.pickerSearchRequestId;

        runInAction(() => {
            this.pickerSearchLoading = true;
        });

        try {
            const documents = await searchCatalogDocuments(query, this.apiMode);
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerDocumentOptions = documents;
                this.pickerSearchLoading = false;
            });
        } catch {
            if (requestId !== this.pickerSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.pickerDocumentOptions = [];
                this.pickerSearchLoading = false;
            });
        }
    };

    private hydrateEntityLabelsFromRules = async (): Promise<void> => {
        const payerIds = new Set<string>();
        const providerIds = new Set<string>();
        const facilityIds = new Set<string>();

        for (const worker of Object.values(this.draftAutomationWorkers)) {
            for (const rule of worker.rules) {
                for (const match of rule.match) {
                    const value = match.value;
                    if ('payerIds' in value) {
                        value.payerIds.forEach(payerId => payerIds.add(payerId));
                    }
                    if ('billingProviderIds' in value) {
                        value.billingProviderIds.forEach(id => providerIds.add(id));
                    }
                    if ('renderingProviderIds' in value) {
                        value.renderingProviderIds.forEach(id => providerIds.add(id));
                    }
                    if ('facilityIds' in value) {
                        value.facilityIds.forEach(id => facilityIds.add(id));
                    }
                }
            }
        }

        for (const statusConfig of Object.values(this.draftConfig)) {
            for (const value of Object.values(statusConfig)) {
                if (!Array.isArray(value)) {
                    continue;
                }

                for (const entry of value) {
                    if (typeof entry === 'string' && entry.trim() !== '') {
                        payerIds.add(entry);
                    }
                }
            }
        }

        const missingPayers = [...payerIds].filter(payerId => !this.payerById[payerId]);
        const missingProviders = [...providerIds].filter(id => !this.providerById[id]);
        const missingFacilities = [...facilityIds].filter(id => !this.facilityById[id]);

        try {
            const [payers, providers, facilities] = await Promise.all([
                missingPayers.length ? fetchCatalogPayersByIds(missingPayers, this.apiMode) : Promise.resolve([]),
                missingProviders.length
                    ? fetchCatalogProvidersByIds(missingProviders, this.apiMode)
                    : Promise.resolve([]),
                missingFacilities.length
                    ? fetchCatalogFacilitiesByIds(missingFacilities, this.apiMode)
                    : Promise.resolve([]),
            ]);

            runInAction(() => {
                if (payers.length) {
                    this.cachePayers(payers);
                }
                if (providers.length) {
                    this.cacheProviders(providers);
                }
                if (facilities.length) {
                    this.cacheFacilities(facilities);
                }
            });
        } catch {
            // Labels fall back to raw IDs when lookup fails.
        }
    };

    @action
    public setDraggingRule = (ruleId: string | null): void => {
        this.draggingRuleId = ruleId;
        if (!ruleId) {
            this.dragOverRuleId = null;
        }
    };

    @action
    public setDragOverRule = (ruleId: string | null): void => {
        this.dragOverRuleId = ruleId;
    };

    @computed
    public get automationRuleNameValidationError(): string | null {
        for (const worker of Object.values(this.draftAutomationWorkers)) {
            for (const rule of worker.rules) {
                const error = getRuleNameValidationError(
                    this.draftAutomationWorkers,
                    rule.name,
                    rule.id,
                );
                if (error) {
                    return error;
                }
            }
        }
        return null;
    }

    @computed
    public get automationMatchValidationError(): string | null {
        return getAutomationMatchValidationError(this.draftAutomationWorkers, this.savedAutomationWorkers);
    }

    @computed
    public get automationActionValidationError(): string | null {
        return getAutomationActionValidationError(this.draftAutomationWorkers);
    }

    @computed
    public get automationRuleValidationError(): string | null {
        return (
            this.automationRuleNameValidationError ??
            this.automationMatchValidationError ??
            this.automationActionValidationError
        );
    }

    public getRuleNameError = (ruleId: string, name: string): string | null => {
        return getRuleNameValidationError(this.draftAutomationWorkers, name, ruleId);
    };

    /** Action-value error for a rule (currently: EXECUTE_ENTER_FUNCTION needs a non-blank function body). */
    public getRuleActionError = (ruleId: string): string | null => {
        const rule = this.findRule(ruleId);
        return rule ? getRuleActionValidationError(rule) : null;
    };

    public getMatchBlockError = (ruleId: string, matchIndex: number): string | null => {
        const workerId = this.selectedAutomationWorkerId;
        if (!workerId) {
            return null;
        }

        const rule = this.draftAutomationWorkers[workerId].rules.find(item => item.id === ruleId);
        const match = rule?.match[matchIndex];
        if (!rule || !match) {
            return null;
        }

        const carcPairRequired = getCarcPairRequirements(
            ruleId,
            rule.match,
            this.savedAutomationWorkers[workerId]?.rules,
        );

        return getMatchBlockValidationError(match, { requireCarcPair: carcPairRequired[matchIndex] });
    };

    @action
    public addRule = (): void => {
        if (!this.canMutate()) {
            return;
        }

        const workerId = this.selectedAutomationWorkerId;
        if (!workerId) {
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        const rule = createEmptyRule(
            worker.scope,
            createUniqueDefaultRuleName(this.draftAutomationWorkers),
        );
        this.updateWorkerRules(workerId, [...worker.rules, rule]);
        this.expandedRuleId = rule.id;
    };

    /**
     * Appends a rule its caller built in full, to a named worker, without saving.
     *
     * For surfaces outside the Workers page that compose a rule themselves — the denials page's
     * "Configure worker" dialog. The Workers page keeps `addRule`, which seeds an empty rule on the
     * selected worker. Additive on purpose: both consoles' Workers pages share this store.
     */
    @action
    public appendAutomationRule = (workerId: AutomationWorkerId, rule: AutomationRule): void => {
        if (!this.canMutate()) {
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        this.updateWorkerRules(workerId, [...worker.rules, rule]);
    };

    /**
     * Sets one worker's whole rule list, in order, without saving — the denials page's rule editor,
     * which creates, edits and REORDERS. Order is priority: the parser reads rules by `priority` and
     * persistence writes `index + 1`, and the server refuses two rules sharing one.
     *
     * Additive, like `appendAutomationRule`; the Workers page keeps its own per-rule actions.
     */
    @action
    public replaceAutomationRules = (workerId: AutomationWorkerId, rules: AutomationRule[]): void => {
        if (!this.canMutate()) {
            return;
        }

        this.updateWorkerRules(workerId, rules);
    };

    @action
    public deleteRule = (ruleId: string): void => {
        const workerId = this.selectedAutomationWorkerId;
        if (!workerId) {
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        this.updateWorkerRules(
            workerId,
            worker.rules.filter(rule => rule.id !== ruleId),
        );
        if (this.expandedRuleId === ruleId) {
            this.expandedRuleId = null;
        }
    };

    @action
    public moveRule = (ruleId: string, direction: -1 | 1): void => {
        const workerId = this.selectedAutomationWorkerId;
        if (!workerId) {
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        const index = worker.rules.findIndex(rule => rule.id === ruleId);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= worker.rules.length) {
            return;
        }

        const rules = worker.rules.slice();
        const [item] = rules.splice(index, 1);
        rules.splice(target, 0, item);
        this.updateWorkerRules(workerId, rules);
    };

    @action
    public reorderRule = (targetRuleId: string): void => {
        const workerId = this.selectedAutomationWorkerId;
        const dragId = this.draggingRuleId;
        if (!workerId || !dragId || dragId === targetRuleId) {
            this.draggingRuleId = null;
            this.dragOverRuleId = null;
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        const rules = worker.rules.slice();
        const from = rules.findIndex(rule => rule.id === dragId);
        const to = rules.findIndex(rule => rule.id === targetRuleId);
        if (from < 0 || to < 0) {
            this.draggingRuleId = null;
            this.dragOverRuleId = null;
            return;
        }

        const [item] = rules.splice(from, 1);
        rules.splice(to, 0, item);
        this.updateWorkerRules(workerId, rules);
        this.draggingRuleId = null;
        this.dragOverRuleId = null;
    };

    @action
    public renameRule = (ruleId: string, name: string): void => {
        this.updateRule(ruleId, rule => ({ ...rule, name: clampRuleName(name) }));
    };

    @action
    public setRuleGrouped = (ruleId: string, group: boolean): void => {
        this.updateRule(ruleId, rule => ({ ...rule, group }));
    };

    @action
    public changeRuleAction = (ruleId: string, type: RuleActionType): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            action: { type, value: getDefaultActionValue(type) },
        }));
        // Invalidates any AI build still in flight for this rule (PR review, 2026-09-28): without
        // this, a build started under EXECUTE_ENTER_FUNCTION that resolves after the user switches
        // to a different action would still call setRuleActionValue(ruleId, 'function', code),
        // writing a stray `function` key into whatever the new action's value now is.
        this.functionBuildRequestId[ruleId] = (this.functionBuildRequestId[ruleId] ?? 0) + 1;
        // Drops any leftover prompt/mode/error too (PR review, 2026-09-28): the action's own value
        // was just reset above, so a stale `functionBuilders[ruleId]` left at mode: 'code' would
        // show "Change the prompt" — pointing at code that no longer exists — the moment the user
        // switches back to EXECUTE_ENTER_FUNCTION, with no obvious way to reach the prompt/build UI
        // to fix it. `getFunctionBuilder` re-derives a fresh default the next time it's read.
        delete this.functionBuilders[ruleId];
        if (type === 'EXECUTE_ENTER_FUNCTION') {
            void this.loadCoderRules();
        }
    };

    /** The full set of coder rules to skip, replacing whatever was there — see `setRuleActionValue`. */
    @action
    public setDisabledCodingRules = (ruleId: string, ruleNumbers: number[]): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            action: {
                ...rule.action,
                value: { ...rule.action.value, disableCodingRules: normalizeDisabledCodingRules(ruleNumbers) },
            },
        }));
    };

    @action
    public setRuleActionValue = (ruleId: string, field: string, value: unknown): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            action: {
                ...rule.action,
                value: { ...rule.action.value, [field]: value },
            },
        }));
    };

    /** Read-only — never creates an entry. Derives the starting mode from whether the rule already has code. */
    public getFunctionBuilder = (ruleId: string): FunctionBuilderState => {
        const existing = this.functionBuilders[ruleId];
        if (existing) {
            return existing;
        }

        const fn = this.findRule(ruleId)?.action.value.function;
        const hasCode = typeof fn === 'string' && fn.trim().length > 0;

        return { ...DEFAULT_FUNCTION_BUILDER_STATE, mode: hasCode ? 'code' : 'prompt' };
    };

    @action
    public setFunctionPrompt = (ruleId: string, prompt: string): void => {
        this.functionBuilders[ruleId] = { ...this.getFunctionBuilder(ruleId), prompt, error: null };
    };

    /**
     * The conditions to describe to the function builder for a rule, when its draft MATCH is not all of them. The
     * Denials rule editor keeps a rule's own codes out of the store while it edits the rest (FE-182), so it sets the
     * rule's whole MATCH here; `undefined` clears it. Read only by `buildRuleFunction`.
     */
    private functionMatchContext: Record<string, RuleMatch[]> = {};

    public setFunctionMatchContext = (ruleId: string, match: RuleMatch[] | undefined): void => {
        if (match) {
            this.functionMatchContext[ruleId] = match;
        } else {
            delete this.functionMatchContext[ruleId];
        }
    };

    /** "Change the prompt" — reopens the prompt for editing; the last-built code stays in the editor untouched. */
    @action
    public editFunctionPrompt = (ruleId: string): void => {
        this.functionBuilders[ruleId] = { ...this.getFunctionBuilder(ruleId), mode: 'prompt', error: null };
    };

    /**
     * Sends the prompt to the AI extension and, on success, replaces the rule's function with the
     * result (a rebuild, not a revision — matches "Change the prompt" starting over, per the plan).
     * Never executes the generated code; `checkWorkerFunctionSyntax` only parses it.
     *
     * Like every other async mutation in this store (`loadAssignableUsers`, the picker searches),
     * this assumes the user stays on the same worker while it is in flight — switching workers
     * before it resolves silently drops the result, same limitation `setRuleActionValue` already
     * has via `updateRule`. The per-rule token below guards the narrower, likelier case: the rule
     * was deleted, or a second build for the same rule started, while this one was still running.
     */
    public buildRuleFunction = async (ruleId: string): Promise<void> => {
        const current = this.getFunctionBuilder(ruleId);
        const prompt = current.prompt.trim();
        const rule = this.findRule(ruleId);
        if (!prompt || current.building || !rule) {
            return;
        }

        const requestId = (this.functionBuildRequestId[ruleId] ?? 0) + 1;
        this.functionBuildRequestId[ruleId] = requestId;
        const isStale = () => this.functionBuildRequestId[ruleId] !== requestId;

        runInAction(() => {
            this.functionBuilders[ruleId] = { ...current, building: true, error: null };
        });

        try {
            const side = this.apiMode === 'provider' ? 'Provider' : 'Support';
            const utility = buildWorkerFunctionUtility({ side });
            const dataJson = buildWorkerFunctionContextJson({
                workerId: this.selectedAutomationWorkerId ?? '',
                ruleName: rule.name,
                matchSummary: formatRuleMatchSummary(
                    this.functionMatchContext[ruleId] ?? rule.match,
                    this.catalog,
                    this.payerById,
                    this.providerById,
                    this.facilityById,
                ),
            });

            const result = await aiBridge.chat(
                buildAiConversationId('workerFunction', `${ruleId}-${requestId}`),
                'suggest',
                prompt,
                {
                    entity: {
                        name: 'WorkerRuleFunction',
                        description: 'A JavaScript function that edits a claim payload before it is (re)submitted.',
                    },
                    dataJson,
                },
                utility,
            );

            if (isStale()) {
                return;
            }

            const code = extractGeneratedFunction(result.assistantMessage.content);
            if (!code) {
                runInAction(() => {
                    this.functionBuilders[ruleId] = {
                        ...this.getFunctionBuilder(ruleId),
                        building: false,
                        error: "The AI didn't return a usable function — try rephrasing.",
                    };
                });
                return;
            }

            const syntaxError = checkWorkerFunctionSyntax(code);
            if (syntaxError) {
                runInAction(() => {
                    this.functionBuilders[ruleId] = {
                        ...this.getFunctionBuilder(ruleId),
                        building: false,
                        error: `The generated function has a syntax error: ${syntaxError}`,
                    };
                });
                return;
            }

            if (isStale()) {
                // A newer build for this rule already owns `functionBuilders[ruleId]` — it will
                // resolve its own entry; touching it here would clobber that.
                return;
            }

            // Belt-and-suspenders alongside `isStale()` (PR review, 2026-09-28): re-read the
            // rule's CURRENT action rather than trusting the `rule` captured at the start of this
            // call — even if nothing bumped the request token, applying a function to a rule that
            // is no longer EXECUTE_ENTER_FUNCTION would corrupt whatever action it switched to.
            const stillExecuteFunction =
                resolveRuleActionType(this.findRule(ruleId)?.action.type) === 'EXECUTE_ENTER_FUNCTION';
            if (!stillExecuteFunction) {
                // Nothing else will ever resolve this entry — the rule was deleted, or the user
                // switched to a DIFFERENT worker while this was in flight (`findRule` only looks at
                // `selectedAutomationWorkerId`, so the rule is invisible from here even though it
                // still exists on its own worker). Leaving `building: true` meant "Build the
                // function" showed "Writing the function…" forever if the user came back to this
                // rule (PR review, 2026-09-28) — clear the stale entry instead of abandoning it.
                runInAction(() => {
                    delete this.functionBuilders[ruleId];
                });
                return;
            }

            runInAction(() => {
                this.setRuleActionValue(ruleId, 'function', code);
                this.functionBuilders[ruleId] = {
                    ...this.getFunctionBuilder(ruleId),
                    building: false,
                    mode: 'code',
                    error: null,
                };
            });
        } catch (e: unknown) {
            if (isStale()) {
                return;
            }
            runInAction(() => {
                this.functionBuilders[ruleId] = {
                    ...this.getFunctionBuilder(ruleId),
                    building: false,
                    error: typeof e === 'string' ? e : e instanceof Error ? e.message : 'AI error',
                };
            });
        }
    };

    @action
    public addMatchBlock = (ruleId: string, scope?: RuleMatchScope): void => {
        const workerId = this.selectedAutomationWorkerId;
        if (!workerId) {
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        const nextScope = scope ?? getAvailableMatchScopes(workerId)[0];

        this.updateRule(ruleId, rule => ({
            ...rule,
            match: [...rule.match, createEmptyMatch(nextScope)],
        }));
    };

    @action
    public removeMatchBlock = (ruleId: string, matchIndex: number): void => {
        this.updateRule(ruleId, rule => {
            if (rule.match.length <= 1) {
                return rule;
            }

            const match = rule.match.filter((_, index) => index !== matchIndex);
            return { ...rule, match };
        });
    };

    @action
    public setMatchBlockScope = (ruleId: string, matchIndex: number, scope: RuleMatchScope): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            match: rule.match.map((match, index) => (index === matchIndex ? createEmptyMatch(scope) : match)),
        }));
        this.openPickerKey = null;
        this.pickerQuery = '';
    };

    @action
    public setMatchBlockExclude = (ruleId: string, matchIndex: number, exclude: boolean): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            match: rule.match.map((match, index) => {
                if (index !== matchIndex) {
                    return match;
                }

                // Unchecking drops the key rather than storing `false`. A backend that
                // echoes the config back without it must still compare equal to the draft,
                // otherwise a successful save leaves the screen showing unsaved changes.
                const { exclude: _previous, ...rest } = match;

                return exclude && canExcludeScope(match.scope) ? { ...rest, exclude: true } : rest;
            }),
        }));
    };

    @action
    public addConditionChip = (ruleId: string, matchIndex: number, kind: ConditionKind, text: string): void => {
        if (!text.trim()) {
            return;
        }

        this.updateRule(ruleId, rule => ({
            ...rule,
            match: rule.match.map((match, index) =>
                index === matchIndex ? addConditionValue(match, kind, text) : match,
            ),
        }));
        this.openPickerKey = null;
        this.pickerQuery = '';
    };

    @action
    public removeConditionChip = (ruleId: string, matchIndex: number, kind: ConditionKind, chipIndex: number): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            match: rule.match.map((match, index) =>
                index === matchIndex ? removeConditionValue(match, kind, chipIndex) : match,
            ),
        }));
    };

    public setConditionValues = (
        ruleId: string,
        matchIndex: number,
        kind: ConditionKind,
        values: string[],
    ): void => {
        this.updateRule(ruleId, rule => ({
            ...rule,
            match: rule.match.map((match, index) =>
                index === matchIndex ? replaceConditionValues(match, kind, values) : match,
            ),
        }));
        this.openPickerKey = null;
        this.pickerQuery = '';
    };

    @action
    public addReconsiderationDocument = (ruleId: string, document: ReconsiderationDocument): void => {
        this.updateRule(ruleId, rule => {
            const documents = [
                ...((rule.action.value.documents as ReconsiderationDocument[] | undefined) ?? []),
                document,
            ];
            return {
                ...rule,
                action: { ...rule.action, value: { ...rule.action.value, documents } },
            };
        });
    };

    @action
    public removeReconsiderationDocument = (ruleId: string, documentIndex: number): void => {
        this.updateRule(ruleId, rule => {
            const documents = [...((rule.action.value.documents as ReconsiderationDocument[] | undefined) ?? [])];
            documents.splice(documentIndex, 1);
            return {
                ...rule,
                action: { ...rule.action, value: { ...rule.action.value, documents } },
            };
        });
    };

    public uploadReconsiderationDocumentFile = async (ruleId: string, file: File): Promise<void> => {
        if (!this.canMutate() || this.documentUploading) {
            return;
        }

        runInAction(() => {
            this.documentUploading = true;
        });

        try {
            const documentType =
                this.apiMode === 'support'
                    ? SupportDocumentType.CLAIMRECONSIDERATION
                    : ProviderCreateDocumentType.MESSAGEDOCUMENT;
            const documentId = await this.documentsStore.uploadDocumentFile(file, { documentType });

            runInAction(() => {
                const existingDocuments =
                    (this.getRuleById(ruleId)?.action.value.documents as ReconsiderationDocument[] | undefined) ?? [];
                if (!existingDocuments.some(document => document.documentId === documentId)) {
                    this.addReconsiderationDocument(ruleId, {
                        documentId,
                        name: sanitizeFilename(file.name),
                    });
                }
                this.documentUploading = false;
            });
        } catch {
            runInAction(() => {
                this.documentUploading = false;
            });
        }
    };

    private getRuleById(ruleId: string): AutomationRule | undefined {
        for (const worker of Object.values(this.draftAutomationWorkers)) {
            const rule = worker.rules.find(candidate => candidate.id === ruleId);
            if (rule) {
                return rule;
            }
        }

        return undefined;
    }

    public getConditionValuesForMatch = (rule: AutomationRule, matchIndex: number, kind: ConditionKind): string[] => {
        const match = rule.match[matchIndex];
        if (!match) {
            return [];
        }

        return getConditionValues(match, kind);
    };

    public getCatalogUsage = (ruleId: string, matchIndex: number, kind: ConditionKind, value: string): CatalogUsage => {
        const worker = this.selectedAutomationWorker;
        if (!worker) {
            return { count: 0, ruleNames: [], ruleNumbers: [] };
        }

        return getCatalogValueUsage(worker, ruleId, kind, value);
    };

    public getAvailableMatchScopesForWorker = (): RuleMatchScope[] => {
        if (!this.selectedAutomationWorkerId) {
            return [];
        }

        return getAvailableMatchScopes(this.selectedAutomationWorkerId);
    };

    @action
    public setAssignedUsers = (ruleId: string, bankUserIds: string[]): void => {
        const assigned = [...new Set(bankUserIds.map(id => id.trim()).filter(Boolean))];

        this.updateRule(ruleId, rule => ({
            ...rule,
            action: { ...rule.action, value: { ...rule.action.value, assigned } },
        }));
    };

    public loadAssignableUsers = async (): Promise<void> => {
        if (this.assignableUsersLoading) {
            return;
        }

        runInAction(() => {
            this.assignableUsersLoading = true;
        });

        try {
            const users = await fetchAssignableUsers(this.apiMode);
            runInAction(() => {
                this.assignableUsers = users;
                this.assignableUsersLoading = false;
            });
            this.migrateAssignedEmailsToBankUserIds();
        } catch {
            runInAction(() => {
                this.assignableUsers = [];
                this.assignableUsersLoading = false;
            });
        }
    };

    /**
     * Unlike `loadAssignableUsers` (needed by the common CREATE_MESSAGE action, so it refreshes on
     * every rule expand), coder rules are only needed by EXECUTE_ENTER_FUNCTION — fetch once and
     * cache, not on every expand of every rule.
     */
    public loadCoderRules = async (): Promise<void> => {
        if (this.coderRulesLoading || this.coderRulesLoaded) {
            return;
        }

        runInAction(() => {
            this.coderRulesLoading = true;
            this.coderRulesError = null;
        });

        try {
            const rules = await fetchCoderRules(this.apiMode);
            runInAction(() => {
                this.coderRules = rules;
                this.coderRulesLoading = false;
                this.coderRulesLoaded = true;
            });
        } catch (e: unknown) {
            runInAction(() => {
                this.coderRules = [];
                this.coderRulesLoading = false;
                this.coderRulesError = typeof e === 'string' ? e : e instanceof Error ? e.message : 'Coder rules could not be loaded.';
                // `coderRulesLoaded` stays false — a fetch failure is retried on the next expand
                // or action change, rather than permanently caching the account as "no coder rules".
            });
        }
    };

    private migrateAssignedEmailsToBankUserIds = (): void => {
        if (this.assignableUsers.length === 0) {
            return;
        }

        let changed = false;
        const nextWorkers: Record<AutomationWorkerId, AutomationWorker> = {
            ...this.draftAutomationWorkers,
        };

        for (const workerId of Object.keys(nextWorkers) as AutomationWorkerId[]) {
            const worker = nextWorkers[workerId];
            let workerChanged = false;
            const nextRules = worker.rules.map(rule => {
                if (rule.action.type !== 'CREATE_MESSAGE') {
                    return rule;
                }

                const assigned = (rule.action.value.assigned as string[] | undefined) ?? [];
                if (assigned.length === 0) {
                    return rule;
                }

                const migrated = assigned.map(value => {
                    if (this.assignableUsers.some(user => user.bankUserId === value)) {
                        return value;
                    }

                    return resolveAssignableUserInput(value, this.assignableUsers) ?? value;
                });
                const deduped = [...new Set(migrated)];

                if (deduped.length === assigned.length && deduped.every((id, index) => id === assigned[index])) {
                    return rule;
                }

                workerChanged = true;
                return {
                    ...rule,
                    action: {
                        ...rule.action,
                        value: { ...rule.action.value, assigned: deduped },
                    },
                };
            });

            if (workerChanged) {
                changed = true;
                nextWorkers[workerId] = { ...worker, rules: nextRules };
            }
        }

        if (changed) {
            runInAction(() => {
                this.draftAutomationWorkers = nextWorkers;
                this.markDirty();
            });
        }
    };

    public getConditionValues = (rule: AutomationRule, matchIndex: number, kind: ConditionKind): string[] => {
        return this.getConditionValuesForMatch(rule, matchIndex, kind);
    };

    @action
    public importCatalog = async (): Promise<void> => {
        await this.loadCatalog();
    };

    public loadCatalog = async (): Promise<void> => {
        if (this.catalogImporting) {
            return;
        }

        runInAction(() => {
            this.catalogImporting = true;
            this.saveError = null;
        });

        try {
            const catalog = await fetchEnterCatalog();
            runInAction(() => {
                this.catalog = catalog;
                this.importedAtLabel = formatCatalogSyncLabel();
                this.catalogImporting = false;
            });
            void this.hydrateEntityLabelsFromRules();
        } catch (err) {
            runInAction(() => {
                this.catalogImporting = false;
                this.saveError = err instanceof Error ? err.message : 'Failed to sync catalog';
            });
        }
    };

    @action
    public discardChanges = (): void => {
        this.draftConfig = cloneWorkflowConfigMap(this.savedConfig);
        this.draftAutomationWorkers = cloneAutomationWorkers(this.savedAutomationWorkers);
        this.dirty = false;
        this.saveError = null;
        this.expandedRuleId = null;
        this.openPickerKey = null;
        this.pickerQuery = '';
    };

    public fetchConfiguration = async (): Promise<void> => {
        runInAction(() => {
            this.loading = true;
            this.error = null;
            this.saveError = null;
        });

        try {
            const [config, options] = await Promise.all([
                fetchClaimWorkflowConfiguration(this.apiMode),
                fetchClaimWorkflowConfigurationOptions(this.apiMode),
            ]);
            runInAction(() => {
                this.configuration = parseWorkerWorkflowConfiguration(config);
                this.configurationOptions = options;
                this.initializeDraftFromConfiguration();
                this.selectInitialStatus();
            });

            // Empty account config: create immediately with option defaults (no user Save needed).
            if (
                !this.readOnly &&
                !hasPersistedConfiguration(this.configuration, this.savedConfig) &&
                this.hasRecommendedDefaultUpdatesOnly
            ) {
                await this.createConfigurationWithDefaults();
            }

            runInAction(() => {
                this.loading = false;
            });
            void this.loadCatalog();
            void this.loadAssignableUsers();
            void this.hydrateEntityLabelsFromRules();
        } catch (err) {
            runInAction(() => {
                this.error = err instanceof Error ? err.message : 'Failed to fetch configuration';
                this.loading = false;
                this.selectedStatus = null;
                this.selectedWorkerType = 'status';
                this.selectedAutomationWorkerId = null;
                this.draftConfig = {};
                this.savedConfig = {};
                this.draftAutomationWorkers = createEmptyAutomationWorkers();
                this.savedAutomationWorkers = createEmptyAutomationWorkers();
                this.dirty = false;
            });
        }
    };

    /**
     * POSTs a new configuration seeded from options-endpoint defaults.
     * Used when GET returns an empty config shell (no createDate / no config keys).
     */
    private createConfigurationWithDefaults = async (): Promise<void> => {
        if (!this.canMutate() || this.saving) {
            return;
        }

        const validationError = this.automationRuleValidationError;
        if (validationError) {
            runInAction(() => {
                this.saveError = validationError;
            });
            return;
        }

        runInAction(() => {
            this.saving = true;
            this.saveError = null;
        });

        try {
            // Full create-or-replace POST; keep saved empty so hasPersistedConfiguration
            // still treats this as an initial create for the empty-account path.
            const draftPayload = mergeWorkflowConfiguration(
                toJS(this.draftConfig),
                toJS(this.draftAutomationWorkers),
            );
            const saved = await saveClaimWorkflowConfiguration(
                draftPayload,
                {},
                this.configuration,
                this.apiMode,
                this.configurationOptions,
            );

            runInAction(() => {
                this.applySavedConfiguration(saved);
                this.saving = false;
            });
        } catch (err) {
            runInAction(() => {
                this.saveError = err instanceof Error ? err.message : 'Failed to create configuration';
                this.saving = false;
            });
        }
    };

    public saveConfiguration = async (): Promise<boolean> => {
        if (!this.canMutate() || !this.dirty || this.saving) {
            return false;
        }

        const validationError = this.automationRuleValidationError;
        if (validationError) {
            runInAction(() => {
                this.saveError = validationError;
            });
            return false;
        }

        runInAction(() => {
            this.saving = true;
            this.saveError = null;
        });

        try {
            const draftPayload = mergeWorkflowConfiguration(
                toJS(this.draftConfig),
                toJS(this.draftAutomationWorkers),
            );
            const savedPayload = mergeWorkflowConfiguration(
                toJS(this.savedConfig),
                toJS(this.savedAutomationWorkers),
            );
            const saved = await saveClaimWorkflowConfiguration(
                draftPayload,
                savedPayload,
                this.configuration,
                this.apiMode,
                this.configurationOptions,
            );

            runInAction(() => {
                this.applySavedConfiguration(saved);
                this.saving = false;
            });

            return true;
        } catch (err) {
            runInAction(() => {
                this.saveError = err instanceof Error ? err.message : 'Failed to save configuration';
                this.saving = false;
            });
            return false;
        }
    };

    @action
    private applySavedConfiguration = (saved: WorkerWorkflowConfiguration): void => {
        const parsed = splitWorkflowConfiguration(saved.config);
        const savedConfig = coerceNumericWorkflowConfigValues(
            cloneWorkflowConfigMap(parsed.workflowConfig),
            this.configurationOptions,
        );
        const savedWorkers = cloneAutomationWorkers(parsed.automationWorkers);
        this.savedConfig = savedConfig;
        this.draftConfig = cloneWorkflowConfigMap(savedConfig);
        this.savedAutomationWorkers = savedWorkers;
        this.draftAutomationWorkers = cloneAutomationWorkers(savedWorkers);
        this.configuration = {
            ...this.configuration,
            createDate: saved.createDate ?? this.configuration?.createDate,
            active: saved.active ?? this.configuration?.active,
            createdBy: saved.createdBy ?? this.configuration?.createdBy,
            config: mergeWorkflowConfiguration(savedConfig, savedWorkers),
        };
        this.dirty = false;
        this.saveError = null;
    };

    @action
    private initializeDraftFromConfiguration = (): void => {
        const parsed = splitWorkflowConfiguration(this.configuration?.config);
        const savedConfig = coerceNumericWorkflowConfigValues(
            cloneWorkflowConfigMap(parsed.workflowConfig),
            this.configurationOptions,
        );
        const workers = cloneAutomationWorkers(parsed.automationWorkers);

        // New configs: seed draft with option defaults. Existing configs: keep set values;
        // fill unset keys with defaults so they appear as unsaved changes until saved.
        this.savedConfig = savedConfig;
        this.draftConfig = applyWorkflowOptionDefaults(savedConfig, this.configurationOptions, this.apiMode);
        this.draftAutomationWorkers = workers;
        this.savedAutomationWorkers = cloneAutomationWorkers(workers);
        this.saveError = null;
        this.markDirty();
    };

    @action
    private selectInitialStatus = (): void => {
        if (this.selectedStatus !== null || this.selectedAutomationWorkerId !== null) {
            return;
        }

        const firstStatus = this.sortedStatuses[0]?.workflowStatus;
        if (firstStatus) {
            this.selectedStatus = firstStatus;
            return;
        }

        // No workflow statuses to land on, but the automation workers are always present — they
        // come from client-side definitions rather than the options endpoint. Selecting the first
        // one beats opening on "Select a workflow status to view configuration." beside a list
        // that plainly has workers in it.
        //
        // `sortedStatuses` reads `configurationOptions`, so this branch is only reached when that
        // endpoint returned nothing — which today means demo mode. Where statuses exist the
        // behaviour above is unchanged.
        const firstWorker = (Object.values(this.draftAutomationWorkers) as AutomationWorker[])[0];
        if (firstWorker) {
            this.selectedWorkerType = 'automation';
            this.selectedAutomationWorkerId = firstWorker.id;
            return;
        }

        this.selectedStatus = null;
    };

    @action
    private updateWorkerRules = (workerId: AutomationWorkerId, rules: AutomationRule[]): void => {
        if (!this.canMutate()) {
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        this.draftAutomationWorkers = {
            ...this.draftAutomationWorkers,
            [workerId]: { ...worker, rules },
        };
        this.markDirty();
        this.saveError = null;
    };

    @action
    private updateRule = (ruleId: string, updater: (rule: AutomationRule) => AutomationRule): void => {
        const workerId = this.selectedAutomationWorkerId;
        if (!workerId) {
            return;
        }

        const worker = this.draftAutomationWorkers[workerId];
        this.updateWorkerRules(
            workerId,
            worker.rules.map(rule => (rule.id === ruleId ? updater(rule) : rule)),
        );
    };

    /** The rule by id within the SELECTED automation worker — every rule card operates on one. */
    private findRule = (ruleId: string): AutomationRule | undefined => {
        const workerId = this.selectedAutomationWorkerId;
        if (!workerId) {
            return undefined;
        }
        return this.draftAutomationWorkers[workerId]?.rules.find(rule => rule.id === ruleId);
    };

    @action
    private markDirty = (): void => {
        this.dirty =
            !workflowConfigsEqual(this.draftConfig, this.savedConfig) ||
            !automationWorkersEqual(this.draftAutomationWorkers, this.savedAutomationWorkers);
    };
}

export function createWorkerStore(options: WorkerStoreOptions = {}): WorkerStore {
    return new WorkerStore({
        apiMode: options.apiMode ?? 'support',
        readOnly: options.readOnly ?? false,
    });
}
